// src/hooks/useCompanyValidation.js
import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "./useAuth";

// A company document is readable only by its own members (firestore.rules), so the
// slug is checked against the signed-in user's own claims and the company is read by
// id — a slug query across `companies` is denied (PRD_CompanyAccessRules C2).
export const useCompanyValidation = (slug) => {
  const { user, loading: authLoading } = useAuth();
  const companyId = user?.userData?.companyId;
  const companySlug = user?.userData?.companySlug;
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const validateCompany = async () => {
      if (!slug) {
        setError("No company slug provided");
        setLoading(false);
        return;
      }

      if (authLoading) return;

      if (!companyId || slug !== companySlug) {
        setError("Company not found");
        setCompany(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const companyDoc = await getDoc(doc(db, "companies", companyId));

        if (!companyDoc.exists()) {
          setError("Company not found");
          setCompany(null);
        } else {
          setCompany({
            id: companyDoc.id,
            ...companyDoc.data(),
          });
          setError(null);
        }
      } catch (err) {
        console.error("Company validation error:", err);

        // More specific error handling
        if (err.code === "permission-denied") {
          setError("Permission denied - please check Firestore security rules");
        } else if (err.code === "unavailable") {
          setError("Database unavailable - please try again");
        } else {
          setError("Failed to validate company");
        }
        setCompany(null);
      } finally {
        setLoading(false);
      }
    };

    validateCompany();
  }, [slug, authLoading, companyId, companySlug]);

  return { company, loading, error };
};
